import type { Lang } from "./index";

// Copy for ADHD Home mode (ADR-0015, design handoff). Each language is
// written natively, not literally translated. Tone is supportive, never
// controlling: no percentages, no "you missed", no alarm wording
// (design handoff §"Tone of voice").

export type AdhdCopy = {
  // --- ADHDPill (SPOKÓJ · ON/OFF toggle) ---
  pillLabel: string; // the word before the dot, e.g. "SPOKÓJ"
  pillOn: string; // suffix when ADHD mode is active
  pillOff: string; // suffix when ADHD mode is off
  pillAriaOn: string; // aria-label to turn the mode OFF
  pillAriaOff: string; // aria-label to turn the mode ON
  // --- ADHDChooser (CISZA | SYGNAŁ | KROK) ---
  variantA: string;
  variantB: string;
  variantC: string;
  chooserAria: string; // group label
  variantAriaA: string;
  variantAriaB: string;
  variantAriaC: string;
  // --- ADHDNav ---
  navMore: string; // "WIĘCEJ ↗"
  navMoreAria: string;
  navAddAria: string;
  // --- Variant A · Cisza ---
  aEyebrow: string; // "ZOSTAŁO DZIŚ"
  aUnit: string; // "kcal"
  aSubBigWindow: string;
  aSubEvening: string;
  aSubLittleLeft: string;
  aSubLimitDone: string;
  aSubFreshStart: string; // empty state (no data today)
  // --- Variant B · Sygnał ---
  bWordOk: string; // "ZJEDZ"
  bWordSoft: string; // "POMAŁU"
  bWordStop: string; // "CISZA"
  bWordStart: string; // empty state word
  bSubOk: (kcal: number) => string;
  bSubSoft: (kcal: number) => string;
  bSubStop: string;
  bSubStart: string; // empty state sub
  bStateAriaOk: string;
  bStateAriaSoft: string;
  bStateAriaStop: string;
  // --- Variant C · Krok ---
  cEyebrow: string; // "CO TERAZ"
  cHeadlineLunch1: string; // first line, e.g. "Pora na"
  cHeadlineLunch2: string; // second line, e.g. "obiad"
  cHeadlineRest1: string; // over-limit headline line 1
  cHeadlineRest2: string; // over-limit headline line 2
  cIntro: (kcal: number) => string;
  cIntroRest: string; // over-limit intro
  cCardTop: string; // "★ TWÓJ TOP"
  cCardMeta: (kcal: number) => string; // "ok 580 kcal"
  cCardFromFridge: string; // "Z LODÓWKI" badge
  cSaveCta: string; // "ZAPISZ JAKO ZJEDZONE"
  cShowOthers: string; // "POKAŻ INNE →"
  cLoading: string; // suggestion loading text
  cPickAriaPrefix: string; // aria-label prefix for the card CTA
  // empty / edge states for variant C
  cEmptyTitle: string; // when no suggestion came back
  cEmptyBody: string;
  cRestCardName: string; // "Herbata?" card name when over the limit
  cFreshCardName: string; // fresh-user card name (no preferences)
  cErrorRetry: string; // retry button
  // 429 Too Many Requests from the AI suggestion endpoint — calmer copy,
  // no retry button (the aiGuard burst window resets after ~30 s).
  cRateLimitedBody: string;
  // --- onboarding question ---
  onbEyebrow: string;
  onbQuestion: string;
  onbAnswerAll: string; // → enabled:false
  onbAnswerCalm: string; // → enabled:true, variant A
  onbAnswerSignal: string; // → enabled:true, variant B
  onbAnswerStep: string; // → enabled:true, variant C
  onbSkip: string;
  onbSaving: string;
  // --- settings (dashboard) section ---
  setEyebrow: string;
  setEnableLabel: string;
  setEnableHint: string;
  setOn: string;
  setOff: string;
  setVariantLabel: string;
};

const pl: AdhdCopy = {
  pillLabel: "SPOKÓJ",
  pillOn: "· ON",
  pillOff: "· OFF",
  pillAriaOn: "Wyłącz tryb spokoju",
  pillAriaOff: "Włącz tryb spokoju",
  variantA: "Cisza",
  variantB: "Sygnał",
  variantC: "Krok",
  chooserAria: "Wybierz widok trybu spokoju",
  variantAriaA: "Widok Cisza, jedna liczba",
  variantAriaB: "Widok Sygnał, jedno słowo",
  variantAriaC: "Widok Krok, jedna propozycja",
  navMore: "WIĘCEJ ↗",
  navMoreAria: "Wróć do pełnego widoku",
  navAddAria: "Dodaj posiłek",
  aEyebrow: "Zostało dziś",
  aUnit: "kcal",
  aSubBigWindow: "Spokojnie. Masz spore okno na obiad",
  aSubEvening: "Wieczór jest twój. Zjedz coś lekkiego",
  aSubLittleLeft: "Mało zostało. Może herbata",
  aSubLimitDone: "Limit zjedzony. Czas na relaks",
  aSubFreshStart: "Zaczynamy. Co dziś",
  bWordOk: "ZJEDZ",
  bWordSoft: "POMAŁU",
  bWordStop: "CISZA",
  bWordStart: "ZACZNIJ",
  bSubOk: (kcal) => `masz ${kcal} kcal — to spory obiad`,
  bSubSoft: (kcal) => `zostało ${kcal} kcal — coś lekkiego`,
  bSubStop: "limit zjedzony — woda i spokój",
  bSubStart: "dziś jeszcze nic",
  bStateAriaOk: "Stan: możesz jeść",
  bStateAriaSoft: "Stan: pomału",
  bStateAriaStop: "Stan: cisza",
  cEyebrow: "Co teraz",
  cHeadlineLunch1: "Pora na",
  cHeadlineLunch2: "obiad",
  cHeadlineRest1: "Pora",
  cHeadlineRest2: "odpocząć",
  cIntro: (kcal) => `Masz ${kcal} kcal. Wystarczy ci na coś porządnego.`,
  cIntroRest: "Limit zjedzony. Dziś już nic nie musisz liczyć.",
  cCardTop: "★ Twój top",
  cCardMeta: (kcal) => `ok ${kcal} kcal`,
  cCardFromFridge: "Z lodówki",
  cSaveCta: "Zapisz jako zjedzone",
  cShowOthers: "Pokaż inne →",
  cLoading: "Zaglądam do lodówki...",
  cPickAriaPrefix: "Propozycja",
  cEmptyTitle: "Coś prostego",
  cEmptyBody: "Nie mam teraz propozycji. Dodaj posiłek po swojemu.",
  cRestCardName: "Herbata i chwila spokoju",
  cFreshCardName: "Coś prostego na początek",
  cErrorRetry: "Spróbuj jeszcze raz",
  cRateLimitedBody: "Chwila. Za dużo prób pod rząd. Wróć tu za moment.",
  onbEyebrow: "Jeszcze jedno",
  onbQuestion: "Lubisz dużo danych, czy wolisz spokój?",
  onbAnswerAll: "Pokaż mi wszystko",
  onbAnswerCalm: "Spokój i cisza",
  onbAnswerSignal: "Tylko decyzje",
  onbAnswerStep: "Powiedz mi co zjeść",
  onbSkip: "Pomiń na razie",
  onbSaving: "Zapisuję...",
  setEyebrow: "Wygląd ekranu głównego",
  setEnableLabel: "Tryb spokoju",
  setEnableHint: "Mniej liczb, jedno pytanie na ekran.",
  setOn: "Włączony",
  setOff: "Wyłączony",
  setVariantLabel: "Widok",
};

const en: AdhdCopy = {
  pillLabel: "CALM",
  pillOn: "· ON",
  pillOff: "· OFF",
  pillAriaOn: "Turn calm mode off",
  pillAriaOff: "Turn calm mode on",
  variantA: "Quiet",
  variantB: "Signal",
  variantC: "Step",
  chooserAria: "Choose calm mode view",
  variantAriaA: "Quiet view, one number",
  variantAriaB: "Signal view, one word",
  variantAriaC: "Step view, one suggestion",
  navMore: "MORE ↗",
  navMoreAria: "Back to the full view",
  navAddAria: "Add a meal",
  aEyebrow: "Left today",
  aUnit: "kcal",
  aSubBigWindow: "Take it easy. Plenty of room for a meal",
  aSubEvening: "The evening is yours. Have something light",
  aSubLittleLeft: "Not much left. Maybe some tea",
  aSubLimitDone: "Goal wrapped up. Time to relax",
  aSubFreshStart: "Let's begin. What's today",
  bWordOk: "EAT",
  bWordSoft: "EASY",
  bWordStop: "REST",
  bWordStart: "START",
  bSubOk: (kcal) => `you have ${kcal} kcal — room for a real meal`,
  bSubSoft: (kcal) => `${kcal} kcal left — something light`,
  bSubStop: "goal wrapped up — water and rest",
  bSubStart: "nothing logged yet today",
  bStateAriaOk: "State: you can eat",
  bStateAriaSoft: "State: take it easy",
  bStateAriaStop: "State: rest",
  cEyebrow: "What now",
  cHeadlineLunch1: "Time for",
  cHeadlineLunch2: "a meal",
  cHeadlineRest1: "Time to",
  cHeadlineRest2: "rest",
  cIntro: (kcal) => `You have ${kcal} kcal. Enough for something proper.`,
  cIntroRest: "Goal wrapped up. Nothing left to count today.",
  cCardTop: "★ Your top pick",
  cCardMeta: (kcal) => `about ${kcal} kcal`,
  cCardFromFridge: "From the fridge",
  cSaveCta: "Save as eaten",
  cShowOthers: "Show others →",
  cLoading: "Looking in the fridge...",
  cPickAriaPrefix: "Suggestion",
  cEmptyTitle: "Something simple",
  cEmptyBody: "No suggestion right now. Add a meal your own way.",
  cRestCardName: "Tea and a calm moment",
  cFreshCardName: "Something simple to start",
  cErrorRetry: "Give it another go",
  cRateLimitedBody: "Easy. Too many tries in a row. Come back in a moment.",
  onbEyebrow: "One more thing",
  onbQuestion: "Do you like lots of data, or prefer calm?",
  onbAnswerAll: "Show me everything",
  onbAnswerCalm: "Calm and quiet",
  onbAnswerSignal: "Just decisions",
  onbAnswerStep: "Tell me what to eat",
  onbSkip: "Skip for now",
  onbSaving: "Saving...",
  setEyebrow: "Home screen look",
  setEnableLabel: "Calm mode",
  setEnableHint: "Fewer numbers, one question per screen.",
  setOn: "On",
  setOff: "Off",
  setVariantLabel: "View",
};

const de: AdhdCopy = {
  pillLabel: "RUHE",
  pillOn: "· AN",
  pillOff: "· AUS",
  pillAriaOn: "Ruhemodus ausschalten",
  pillAriaOff: "Ruhemodus einschalten",
  variantA: "Stille",
  variantB: "Signal",
  variantC: "Schritt",
  chooserAria: "Ansicht des Ruhemodus wählen",
  variantAriaA: "Ansicht Stille, eine Zahl",
  variantAriaB: "Ansicht Signal, ein Wort",
  variantAriaC: "Ansicht Schritt, ein Vorschlag",
  navMore: "MEHR ↗",
  navMoreAria: "Zurück zur vollen Ansicht",
  navAddAria: "Mahlzeit hinzufügen",
  aEyebrow: "Heute übrig",
  aUnit: "kcal",
  aSubBigWindow: "Ganz ruhig. Reichlich Platz für eine Mahlzeit",
  aSubEvening: "Der Abend gehört dir. Iss etwas Leichtes",
  aSubLittleLeft: "Wenig übrig. Vielleicht ein Tee",
  aSubLimitDone: "Ziel abgeschlossen. Zeit zu entspannen",
  aSubFreshStart: "Fangen wir an. Was gibt es heute",
  bWordOk: "ISS",
  bWordSoft: "RUHIG",
  bWordStop: "STILLE",
  bWordStart: "START",
  bSubOk: (kcal) => `du hast ${kcal} kcal — Platz für eine richtige Mahlzeit`,
  bSubSoft: (kcal) => `${kcal} kcal übrig — etwas Leichtes`,
  bSubStop: "Ziel abgeschlossen — Wasser und Ruhe",
  bSubStart: "heute noch nichts erfasst",
  bStateAriaOk: "Zustand: du kannst essen",
  bStateAriaSoft: "Zustand: ganz ruhig",
  bStateAriaStop: "Zustand: Stille",
  cEyebrow: "Was jetzt",
  cHeadlineLunch1: "Zeit für",
  cHeadlineLunch2: "eine Mahlzeit",
  cHeadlineRest1: "Zeit zum",
  cHeadlineRest2: "Ausruhen",
  cIntro: (kcal) => `Du hast ${kcal} kcal. Genug für etwas Ordentliches.`,
  cIntroRest: "Ziel abgeschlossen. Heute musst du nichts mehr zählen.",
  cCardTop: "★ Dein Top",
  cCardMeta: (kcal) => `ca. ${kcal} kcal`,
  cCardFromFridge: "Aus dem Kühlschrank",
  cSaveCta: "Als gegessen speichern",
  cShowOthers: "Andere zeigen →",
  cLoading: "Schaue in den Kühlschrank...",
  cPickAriaPrefix: "Vorschlag",
  cEmptyTitle: "Etwas Einfaches",
  cEmptyBody: "Gerade kein Vorschlag. Füg eine Mahlzeit auf deine Art hinzu.",
  cRestCardName: "Tee und ein ruhiger Moment",
  cFreshCardName: "Etwas Einfaches zum Anfang",
  cErrorRetry: "Versuch es noch einmal",
  cRateLimitedBody: "Moment. Zu viele Versuche hintereinander. Komm gleich wieder.",
  onbEyebrow: "Noch eine Sache",
  onbQuestion: "Magst du viele Daten oder lieber Ruhe?",
  onbAnswerAll: "Zeig mir alles",
  onbAnswerCalm: "Ruhe und Stille",
  onbAnswerSignal: "Nur Entscheidungen",
  onbAnswerStep: "Sag mir, was ich essen soll",
  onbSkip: "Erstmal überspringen",
  onbSaving: "Speichern...",
  setEyebrow: "Aussehen des Startbildschirms",
  setEnableLabel: "Ruhemodus",
  setEnableHint: "Weniger Zahlen, eine Frage pro Bildschirm.",
  setOn: "An",
  setOff: "Aus",
  setVariantLabel: "Ansicht",
};

export const adhdCopy: Record<Lang, AdhdCopy> = { pl, en, de };
